import { IPoint2d } from '@/editor/math/IPoint2d';
import { EOperateMode, EToolType } from '@/editor/types';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { clientPosToWorldPos } from '@/editor/utils';
import { CommonController2d } from '../CommonController';
import VirtualWallModel from '@/editor/entities/2d/virtualWallModel';
import { IRulerInfo, RulerModel } from '@/editor/entities/2d/rulerModel';
import { DEFAULT_RULER_OFFSET } from '@/editor/constant/ruler';
import { WallPointAbsorbManager } from '@/editor/manager/absorb/wall/wallPointAbsorbManager';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { ESepcialKey } from '@/editor/stores/keyboardStore';

class DrawWall extends CommonController2d {
  protected _currentPoint?: IPoint2d | null;
  protected _movePoint?: IPoint2d;

  private _virtualWallModel = new VirtualWallModel();
  private _virtualWallRuler: RulerModel;
  protected _wallAbsorbManager: WallPointAbsorbManager;
  private _eventDispose: () => void;

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._wallAbsorbManager = new WallPointAbsorbManager(globalStores.wallStore);
    this._wallAbsorbManager.updateWallData();
    this._virtualWallRuler = new RulerModel({
      onChange: this._handleRulerChange.bind(this),
      onCommit: this._handleRulerCommit.bind(this)
    });

    this._eventDispose = editorEvent.on(EditorEventName.KEYBOARD_KEY_PRESS, (key: string) => {
      if (key === ESepcialKey.Escape) {
        if (!this._currentPoint) {
          this._gs.operateInfo = { mode: EOperateMode.EMPTY, toolType: EToolType.empty };
        } else {
          this.clear();
        }
      }
    });
  }

  protected async addWallPoint(point: IPoint2d) {
    if (!this._currentPoint) {
      // 点击第一次
      this._currentPoint = point;

      this._wallAbsorbManager.setOrthogonalData(point); // 更新正交线
      return;
    }

    const startPoint = this._currentPoint;
    const endPoint = point;

    const isAddSuccess = await this._gs.wallOperation.addWall({
      start: startPoint,
      end: endPoint,
      operateMode: EOperateMode.DrawWall
    });
    if (!isAddSuccess) return;

    this._currentPoint = endPoint;

    this._wallAbsorbManager.setOrthogonalData(endPoint); // 更新正交线
    this._wallAbsorbManager.updateWallData(); // 更新墙体数据

    this._virtualWallModel.clear();
    this._virtualWallRuler.clear();
  }

  // 开始/结束绘制墙体时会有很多状态设定
  protected override mouseUp(e: MouseEvent, isDragged: boolean) {
    if (isDragged) return;

    if (e.button === 0) {
      if (!this._movePoint) return;

      void this.addWallPoint(this._movePoint);
      return;
    }
    if (e.button === 2) {
      // 右键
      if (this._currentPoint) {
        this.clear();
      } else {
        // 退出编辑
        this.dispose();
        this.exit();
      }

      return;
    }
  }

  protected override mouseMove(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const point = clientPosToWorldPos(e, camera, renderer);

    const adsorbPoint = this._wallAbsorbManager.calculateAbsorbPointThenRender({ x: point.x, y: point.y });
    this._movePoint = adsorbPoint || point;

    // 更新虚拟墙体和标尺
    this._virtualWallRuler.resetFocusId();
    this._updateVirtualWall(this._movePoint);
  }

  protected _updateVirtualWall(endPoint: IPoint2d) {
    const start = this._currentPoint;

    if (!start) return;

    const virtualWalls = this.getVirtualWalls(start, endPoint);
    this._virtualWallModel.update(virtualWalls);
    this._virtualWallRuler.updateModel(
      virtualWalls
        .filter((wall) => !wall.noRuler)
        .map((wall, index) => ({
          id: `${index}`,
          start: wall.start,
          end: wall.end,
          offset: wall.thickness / 2 + DEFAULT_RULER_OFFSET,
          editable: true,
          focusTrap: true
        })),
      '0'
    );
  }

  // 子类重写
  protected getVirtualWalls(
    start: IPoint2d,
    end: IPoint2d
  ): { start: IPoint2d; end: IPoint2d; thickness: number; noRuler?: boolean }[] {
    return [
      {
        start,
        end,
        thickness: this._gs.paramStore.wallThickness
      }
    ];
  }

  protected _handleRulerChange(id: string, value: number, rulerInfo: IRulerInfo) {
    if (Point2dUtils.distanceTo(rulerInfo.start, rulerInfo.end) < 200) return;

    const direction = Point2dUtils.subtracted(rulerInfo.end, rulerInfo.start);
    const newEndPoint = Point2dUtils.addDistance(rulerInfo.start, value, direction);
    this._movePoint = newEndPoint;
    this._updateVirtualWall(this._movePoint);
  }

  protected _handleRulerCommit(id: string, value: number, rulerInfo: IRulerInfo) {
    if (Point2dUtils.distanceTo(rulerInfo.start, rulerInfo.end) < 200) return;

    const direction = Point2dUtils.subtracted(rulerInfo.end, rulerInfo.start);
    const newEndPoint = Point2dUtils.addDistance(rulerInfo.start, value, direction);
    void this.addWallPoint(newEndPoint);
  }

  clear() {
    this._currentPoint = undefined;
    this._movePoint = undefined;

    this._virtualWallModel.clear();
    this._virtualWallRuler.clear();
    this._wallAbsorbManager.clearOrthogonalData();
    this._wallAbsorbManager.clearModel();
  }

  override dispose() {
    this.clear();
    this._eventDispose();
    this._virtualWallModel.dispose();
    this._virtualWallRuler.dispose();
    this._wallAbsorbManager.dispose();
    super.dispose();
  }
}

export default DrawWall;
